/**
 * Rappels programmés sur l'appareil (application Android)
 *
 * Pourquoi ce fichier existe : la WebView de Capacitor n'implémente pas l'API
 * Push. Dans l'application installée, `window.PushManager` n'existe pas et
 * l'abonnement Web Push échoue — c'est ce qui faisait qu'aucune notification
 * n'arrivait jamais sur le téléphone. Les rappels y sont donc **programmés à
 * l'avance par l'application elle-même**, sans serveur, sans Firebase. C'est le
 * mécanisme déjà en service dans l'autre application de la maison.
 *
 * Ce module n'est que de la colle : le calcul — quelles notifications, quand,
 * avec quel identifiant — vit dans `@foyer/noyau`, où il est testé sans
 * téléphone. Ici on demande la permission, on annule, on programme, on rend
 * compte.
 *
 * Trois principes tenus :
 *
 *  1. **Silencieux sur le web.** `Capacitor.isNativePlatform()` est faux dans un
 *     navigateur : toutes les fonctions rendent un état neutre sans rien tenter.
 *     Le web garde le Web Push, qui y fonctionne réellement.
 *  2. **On n'annule que ses propres notifications.** Le filtre sur `BASE_ID`
 *     évite d'effacer ce qu'un autre greffon aurait programmé.
 *  3. **Meilleur effort.** Un échec ne casse jamais l'écran appelant : il est
 *     retenu et affiché dans le diagnostic, parce qu'une notification qui
 *     n'arrive pas sans qu'on sache pourquoi est le pire des cas.
 */

import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';

import {
  BASE_ID,
  HORIZON_JOURS,
  ID_ESSAI,
  MAX_NOTIFICATIONS,
  type PeriodeVacances,
  type Relance,
  type Tache,
  aujourdhuiParis,
  planifieRappelsLocaux,
} from '@foyer/noyau';

export const estNatif = () => Capacitor.isNativePlatform();

/**
 * Heure d'affichage des rappels. Les dates du noyau sont des jours civils, pas
 * des instants : il faut choisir une heure, et 8 h est le compromis retenu —
 * assez tôt pour agir dans la journée, pas au milieu de la nuit.
 */
const HEURE_RAPPEL = 8;

export interface EtatRappelsLocaux {
  natif: boolean;
  permission: string;
  /** Notifications réellement en attente dans Android. */
  enAttente: number;
  /** Notifications calculées au dernier passage. */
  programmees: number | null;
  /** Écartées par le plafond : à dire, pas à taire. */
  ecartees: number;
  prochaine: { date: string; titre: string } | null;
  derniereSynchro: string | null;
  derniereErreur: string | null;
}

let derniereErreur: string | null = null;
let derniereSynchro: string | null = null;
let programmees: number | null = null;
let ecartees = 0;

async function assurePermission(): Promise<boolean> {
  const p = await LocalNotifications.checkPermissions();
  if (p.display === 'granted') return true;
  const r = await LocalNotifications.requestPermissions();
  return r.display === 'granted';
}

/** Instant d'affichage à partir d'un jour civil. */
function instantDe(date: string): Date {
  const [a, m, j] = date.split('-').map(Number);
  return new Date(a!, m! - 1, j!, HEURE_RAPPEL, 0, 0, 0);
}

export async function etatRappelsLocaux(): Promise<EtatRappelsLocaux> {
  const neutre: EtatRappelsLocaux = {
    natif: false,
    permission: 'sans objet',
    enAttente: 0,
    programmees: null,
    ecartees: 0,
    prochaine: null,
    derniereSynchro: null,
    derniereErreur: null,
  };
  if (!estNatif()) return neutre;

  let permission = '?';
  let enAttente = 0;
  let prochaine: { date: string; titre: string } | null = null;
  try {
    permission = (await LocalNotifications.checkPermissions()).display;
  } catch (e) {
    permission = `erreur : ${(e as Error).message}`;
  }
  try {
    const attente = (await LocalNotifications.getPending()).notifications.filter((n) => n.id >= BASE_ID);
    enAttente = attente.length;
    // Le greffon ne rend pas toujours `schedule.at` : on ne montre le prochain
    // rappel que si l'information existe, plutôt que d'afficher une date fausse.
    const datees = attente
      .map((n) => ({ n, t: n.schedule?.at ? new Date(n.schedule.at).getTime() : Number.NaN }))
      .filter((x) => Number.isFinite(x.t))
      .sort((a, b) => a.t - b.t);
    if (datees.length > 0) {
      prochaine = {
        date: new Date(datees[0]!.t).toLocaleString('fr-FR'),
        titre: datees[0]!.n.title ?? '',
      };
    }
  } catch (e) {
    derniereErreur = (e as Error).message;
  }

  return {
    natif: true,
    permission,
    enAttente,
    programmees,
    ecartees,
    prochaine,
    derniereSynchro,
    derniereErreur,
  };
}

/**
 * (Re)programme tous les rappels depuis les tâches connues.
 *
 * Annuler puis reprogrammer, plutôt que de tenter une mise à jour fine : les
 * identifiants sont déterministes, donc reprogrammer à l'identique est sans
 * effet visible, et cela garantit qu'une tâche supprimée ou décalée ne laisse
 * pas de rappel fantôme. C'est aussi ce qui rend l'opération idempotente.
 */
export async function reprogrammeRappels(
  taches: Tache[],
  periodes: PeriodeVacances[] = [],
  relance: Relance = 'hebdomadaire',
): Promise<EtatRappelsLocaux> {
  if (!estNatif()) return etatRappelsLocaux();

  try {
    if (!(await assurePermission())) {
      derniereErreur = 'Notifications refusées. À autoriser dans les réglages Android de l’application.';
      derniereSynchro = new Date().toLocaleString('fr-FR');
      programmees = 0;
      return etatRappelsLocaux();
    }

    const attente = (await LocalNotifications.getPending()).notifications.filter((n) => n.id >= BASE_ID);
    if (attente.length > 0) {
      await LocalNotifications.cancel({ notifications: attente.map((n) => ({ id: n.id })) });
    }

    const plan = planifieRappelsLocaux(
      taches.map((t) => ({
        id: t.id,
        titre: t.titre,
        echeance: t.echeance,
        rrule: t.rrule,
        recurrenceDepart: t.recurrenceDepart,
        recurrenceBase: t.recurrenceBase,
        ancrageVacances: t.ancrageVacances,
        rappels: t.rappels,
        resiliationLimite: t.resiliationLimite,
        relanceMuette: t.relanceMuette,
        statut: t.statut,
      })),
      {
        aujourdhui: aujourdhuiParis(),
        horizonJours: HORIZON_JOURS,
        max: MAX_NOTIFICATIONS,
        periodes,
        relance,
      },
    );

    // Un rappel dont l'heure est déjà passée aujourd'hui partirait
    // immédiatement : on le laisse tomber plutôt que de sonner pour rien.
    const maintenant = Date.now();
    const aProgrammer = plan.notifications
      .map((n) => ({ n, at: instantDe(n.date) }))
      .filter((x) => x.at.getTime() > maintenant);

    if (aProgrammer.length > 0) {
      await LocalNotifications.schedule({
        notifications: aProgrammer.map(({ n, at }) => ({
          id: n.id,
          title: n.titre,
          body: n.corps,
          schedule: { at, allowWhileIdle: true },
          extra: { tacheId: n.tacheId, echeance: n.echeance },
        })),
      });
    }

    programmees = aProgrammer.length;
    ecartees = plan.ecartees;
    derniereErreur = null;
    derniereSynchro = new Date().toLocaleString('fr-FR');
  } catch (e) {
    derniereErreur = (e as Error).message;
    derniereSynchro = new Date().toLocaleString('fr-FR');
  }

  return etatRappelsLocaux();
}

/** Notification d'essai, dans quelques secondes. Rend « ok » ou le motif d'échec. */
export async function essaiRappelLocal(delaiSecondes = 10): Promise<string> {
  if (!estNatif()) {
    return 'Disponible uniquement dans l’application installée. Sur le web, utilisez le Web Push.';
  }
  try {
    if (!(await assurePermission())) {
      return 'Notifications refusées. À autoriser dans les réglages Android de l’application.';
    }
    const at = new Date(Date.now() + delaiSecondes * 1000);
    await LocalNotifications.schedule({
      notifications: [
        {
          id: ID_ESSAI,
          title: 'Foyer — essai de rappel',
          body: `Si vous lisez ceci, les rappels de l’application fonctionnent (${at.toLocaleTimeString('fr-FR')}).`,
          schedule: { at, allowWhileIdle: true },
        },
      ],
    });
    return 'ok';
  } catch (e) {
    return `Échec : ${(e as Error).message}`;
  }
}
